/* eslint-disable no-console */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { showToast, ToastType } from '@/helpers/toast';
import { XMarkIcon as XMark } from '@/resources';
import {
  useCreateCeremonyTemplateMutation,
  useUpdateCeremonyTemplateForEventMutation
} from '@/services';
import {
  AssetSlot,
  CeremonyTemplateSlot,
  CreateEditCeremonyTemplatePayload,
  Organization
} from '@/types';

type TemplatePopupProps = {
  onClose: () => void;
  onSave: () => void;
  rightAlignment?: string;
  organization: Organization;
  assets: AssetSlot[];
  eventId: number;
  deviceId?: number;
};

export const TemplatePopup = ({
  onClose,
  onSave,
  organization,
  assets,
  eventId,
  deviceId
}: TemplatePopupProps) => {
  const [t] = useTranslation('common');
  const [templateName, setTemplateName] = useState('');

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const { mutate: updateCeremonyTemplate } = useUpdateCeremonyTemplateForEventMutation(
    queryClient,
    {
      onSuccess: () => undefined,
      onError: () => {
        showToast(ToastType.ERROR, t('ceremonyTemplateError'));
      }
    }
  );

  const { mutate: createCeremonyTemplate } = useCreateCeremonyTemplateMutation(queryClient, {
    onSuccess: (response) => {
      updateCeremonyTemplate({
        ceremonyTemplateId: response?.data?.id,
        eventId: eventId,
        deviceId: deviceId as number
      });
      onSave();
      showToast(ToastType.SUCCESS, t('ceremonyTemplateSuccessfullyCreated'));
      onClose();
    },
    onError: () => {
      showToast(ToastType.ERROR, t('ceremonyTemplateError'));
    }
  });

  const buildCeremonyTemplateSlots = (assets: AssetSlot[]): CeremonyTemplateSlot[] => {
    return assets.map((asset) => ({
      id: 0,
      order: asset.order,
      slotTypeId: asset.slotTypeId,
      ceremonyTemplateSlotEvents: (asset.assetSlotEvents ?? [])
        .filter((assetSlotEvent) => assetSlotEvent.assetSlotTrigger !== 'OnStart')
        .map((assetSlotEvent) => ({
          id: 0,
          ceremonyTemplateSlotId: 0,
          assetSlotTrigger: assetSlotEvent.assetSlotTrigger,
          assetSlotHandler: assetSlotEvent.assetSlotHandler,
          value: String(
            assets.find((assetSlot) => assetSlot.id === Number(assetSlotEvent.value))?.order || null
          )
        }))
    }));
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      showToast(ToastType.ERROR, t('templateNameRequired'));
      return;
    }

    const ceremonyTemplatePayload: CreateEditCeremonyTemplatePayload = {
      id: 0,
      name: templateName,
      organizationId: organization.id,
      slots: buildCeremonyTemplateSlots(assets)
    };

    try {
      createCeremonyTemplate(ceremonyTemplatePayload);
    } catch (error) {
      console.error(t('ceremonyTemplateError'), error);
    }
  };

  return (
    <div
      className='absolute left-0 top-0 z-30 flex h-screen w-screen items-center justify-center backdrop-blur-sm'
      onClick={handleOverlayClick}>
      <div className='filter-pop-up-shadow z-50 max-h-full w-full rounded-2xl bg-white p-5 md:w-1/3'>
        <div className='flex items-center justify-between rounded-t'>
          <h3 className='text-xl font-semibold text-primary-blue'>{t('ceremonyTemplate')}</h3>
          <div onClick={onClose}>
            <XMark className='cursor-pointer font-light text-primary-black' />
          </div>
        </div>
        <div className='grid gap-4 py-4'>
          <input
            type='text'
            className='w-full rounded border border-gray-300 p-2'
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={t('templateName')}
          />
        </div>
        <div className='flex flex-row items-center justify-end'>
          <Button
            variant={ButtonVariant.PRIMARY}
            className='px-9 text-sm'
            handleOnClick={handleSubmit}>
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
};
